import React, { useCallback, useMemo } from "react";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutlined";
import SearchIcon from "@mui/icons-material/Search";

import Button, { ButtonTypes } from "../../../../../components/Button";
import { DropdownAction } from "../../../../../components/Dropdown/types";
import { Pagination } from "../../../../../components/Pagination";
import { SortableColumnHeader } from "../../../../../components/SortableColumnHeader";
import { ErrorState } from "../../../../../components/States";
import { useResponsive } from "../../../../../hooks/useResponsive";
import { Holding, valuationStatusOf } from "../../../../../services/holdings";
import { isPendingValuation } from "../../../../../services/valuation";
import { SECTION_CARD_BREAKPOINT } from "../../constants";
import { InlinePositionInsight } from "../ContextRail";
import {
  ALL_SOLD_BODY,
  ALL_SOLD_TITLE,
  CLEAR_FILTERS_LABEL,
  HOLDING_COLUMNS,
  HOLDINGS_PAGE_SIZE,
  NEW_LEDGER_BODY,
  NEW_LEDGER_TITLE,
  NO_MATCH_BODY,
  NO_MATCH_TITLE,
  PRICE_OVERRIDE_ACTION_LABEL,
  SOURCE_COUNTRY_ACTION_LABEL,
  RECORD_FIRST_TRADE_LABEL,
  RECORD_TRADE_LABEL,
} from "./constants";
import { canRepairWithPrivatePrice } from "./holdingDisplay";
import { HoldingCard } from "./HoldingCard";
import { HoldingRow } from "./HoldingRow";
import { assetFacetOptions } from "./holdingsWorkspace";
import { HoldingsToolbar } from "./HoldingsToolbar";
import { SectionFreshnessNote } from "../SectionFreshnessNote";
import { REFRESH_NOTICE_COPY, RefreshNotice } from "../RefreshNotice";
import { SectionEmptyState, SectionSkeletonRows } from "../SectionCard";
import { HoldingsProps } from "./types";
import {
  Badge,
  Body,
  CardList,
  HeaderCell,
  HeaderRow,
  HiddenHeaderLabel,
  HoldingsCard,
  NoticeInset,
  PagerBand,
  PendingStatus,
  Subtitle,
  Table,
  Title,
  TitleBlock,
} from "./styles";

const ERROR_MESSAGE = "We couldn't load the holdings for this account.";

const HoldingsComponent: React.FC<HoldingsProps> = ({
  holdings,
  workspace,
  hasError = false,
  refreshFailed = false,
  isRefreshing = false,
  freshness = null,
  onRetry,
  onTrade,
  onRecordTrade,
  onSetPrice,
  onSetCountry,
}) => {
  const isMobile = useResponsive(SECTION_CARD_BREAKPOINT);
  // A failed read leaves `data` undefined for good, so the error has to be
  // checked first — otherwise the card would claim to be loading forever.
  const isLoading = holdings === undefined && !hasError;
  const {
    assetGroup,
    clearFilters,
    counts,
    filteredRows,
    openRows,
    page,
    pageCount,
    query,
    setAssetGroup,
    setPage,
    setQuery,
    setSort,
    setStatus,
    sort,
    status,
    statusRows,
    toggleSort,
    visibleRows,
    selectedHoldingId,
    toggleHolding,
    clearSelection,
  } = workspace;

  // Counted from the status partition, so the numbers describe the set the facet would show.
  const assetOptions = useMemo(
    () => assetFacetOptions(statusRows, assetGroup),
    [statusRows, assetGroup],
  );

  const retry = async () => {
    await onRetry?.();
  };

  // Built per row rather than once for the section: each action has to carry
  // the position it was opened from. The historical-price action uses the same
  // repairability gate as the inline and mobile entries; without it the kebab
  // menu made an irrelevant historical repair flow reachable for any holding.
  const buildRowActions = useCallback(
    (holding: Holding): DropdownAction[] => {
      const priceAction: DropdownAction[] = onSetPrice
        ? [
            {
              id: "set-my-price",
              label: PRICE_OVERRIDE_ACTION_LABEL,
              icon: "",
              onClick: () => onSetPrice(holding),
              show: canRepairWithPrivatePrice(holding),
            },
          ]
        : [];
      const countryAction: DropdownAction[] = onSetCountry
        ? [
            {
              id: "set-source-country",
              label: SOURCE_COUNTRY_ACTION_LABEL,
              icon: "",
              onClick: () => onSetCountry(holding),
              show: true,
            },
          ]
        : [];
      if (!onTrade) {
        return [...priceAction, ...countryAction];
      }
      // The sell mode offers open positions only, so a closed one would open a select that excludes it.
      const sellAction: DropdownAction[] = holding.isClosed
        ? []
        : [
            {
              id: "sell",
              label: "Sell",
              icon: "",
              onClick: () => onTrade("sell", holding),
              show: true,
            },
          ];
      return [
        ...priceAction,
        ...sellAction,
        {
          id: "dividend",
          label: "Record dividend",
          icon: "",
          onClick: () => onTrade("dividend", holding),
          show: true,
        },
        {
          id: "fee",
          label: "Record fee",
          icon: "",
          onClick: () => onTrade("fee", holding),
          show: true,
        },
        {
          id: "tax",
          label: "Record withholding tax",
          icon: "",
          onClick: () => onTrade("tax", holding),
          show: true,
        },
        ...countryAction,
      ];
    },
    [onTrade, onSetPrice, onSetCountry],
  );

  const isNarrowed = query.trim() !== "" || assetGroup !== "ALL";
  const statusNoun = status === "closed" ? "closed" : "open";
  let badgeText = `${counts.total} ${statusNoun}`;
  if (isNarrowed) {
    badgeText = `${counts.filtered} of ${counts.total}`;
  }
  if (isLoading) {
    badgeText = "Loading";
  } else if (hasError) {
    badgeText = "Unavailable";
  }

  // Counted account-wide rather than over the visible rows: switching to Closed
  // or typing a search must not hide price work that is still going on.
  const pendingCount = openRows.filter((holding) =>
    isPendingValuation(valuationStatusOf(holding)),
  ).length;
  // The "Holding Added" toast auto-dismisses, and a row with no figure in it
  // looks the same as a broken one once it has gone. This line is what keeps a
  // just-recorded trade visibly successful while its first quote lands.
  const pendingMessage =
    pendingCount === 1
      ? "A price is still being fetched for 1 holding — your trades are saved."
      : `Prices are still being fetched for ${pendingCount} holdings — your trades are saved.`;

  const hasAnyHolding = Boolean(holdings?.length);
  // An account whose every position was sold has a full trade history, so the
  // first-position copy would misdescribe it.
  const isAllSold = hasAnyHolding && status === "open" && counts.open === 0;
  // An empty Closed partition lands here deliberately: the status facet is a filter
  // like any other, and Clear filters is what resets it back to Open.
  const isNoMatch = hasAnyHolding && !isAllSold && filteredRows.length === 0;

  // One cell per declared column, so the header row and the column widths read
  // the same list: a column added to one and not the other would misalign every
  // row below it silently. The actions column is unlabelled on screen but still
  // a column — naming it keeps that count honest for a screen reader too.
  const headerCells = HOLDING_COLUMNS.map(
    ({ label, width, labelHidden, sortKey }) => {
      if (sortKey) {
        return (
          <SortableColumnHeader
            key={label}
            column={sortKey}
            label={label}
            width={width}
            variant="section"
            sort={sort}
            onSort={toggleSort}
          />
        );
      }
      const heading = labelHidden ? (
        <HiddenHeaderLabel>{label}</HiddenHeaderLabel>
      ) : (
        label
      );
      return (
        <HeaderCell key={label} scope="col" $width={width}>
          {heading}
        </HeaderCell>
      );
    },
  );

  const emptyTitle = isAllSold ? ALL_SOLD_TITLE : NEW_LEDGER_TITLE;
  const emptyDescription = isAllSold ? ALL_SOLD_BODY : NEW_LEDGER_BODY;
  const recordTradeLabel = isAllSold
    ? RECORD_TRADE_LABEL
    : RECORD_FIRST_TRADE_LABEL;
  const recordTradeAction = onRecordTrade ? (
    <Button type={ButtonTypes.primary} onClick={onRecordTrade}>
      {recordTradeLabel}
    </Button>
  ) : undefined;

  let body: React.ReactNode;
  if (hasError) {
    body = <ErrorState message={ERROR_MESSAGE} onRetry={retry} />;
  } else if (isLoading) {
    body = <SectionSkeletonRows count={4} label="Loading holdings" />;
  } else if (isNoMatch) {
    body = (
      <SectionEmptyState
        icon={<SearchIcon />}
        title={NO_MATCH_TITLE}
        description={NO_MATCH_BODY}
        action={
          <Button type={ButtonTypes.secondary} onClick={clearFilters}>
            {CLEAR_FILTERS_LABEL}
          </Button>
        }
      />
    );
  } else if (filteredRows.length === 0) {
    body = (
      <SectionEmptyState
        icon={<PieChartOutlineIcon />}
        title={emptyTitle}
        description={emptyDescription}
        action={recordTradeAction}
      />
    );
  } else if (isMobile) {
    body = (
      <CardList>
        {visibleRows.map((holding) => {
          const isSelected = holding.id === selectedHoldingId;
          return (
            <React.Fragment key={holding.id}>
              <HoldingCard
                holding={holding}
                actions={buildRowActions(holding)}
                onSetPrice={onSetPrice}
                isSelected={isSelected}
                onToggleSelect={toggleHolding}
              />
              {isSelected && (
                <InlinePositionInsight
                  holding={holding}
                  onClearSelection={clearSelection}
                />
              )}
            </React.Fragment>
          );
        })}
      </CardList>
    );
  } else {
    body = (
      <Table aria-label="Holdings ledger">
        <thead>
          <tr>{headerCells}</tr>
        </thead>
        <tbody>
          {visibleRows.map((holding) => (
            <HoldingRow
              key={holding.id}
              holding={holding}
              actions={buildRowActions(holding)}
              onSetPrice={onSetPrice}
              isSelected={holding.id === selectedHoldingId}
              onToggleSelect={toggleHolding}
            />
          ))}
        </tbody>
      </Table>
    );
  }

  const showWorkspaceControls = hasAnyHolding && !isLoading && !hasError;
  const pageFrom = page * HOLDINGS_PAGE_SIZE + 1;
  const pageTo = Math.min(
    pageFrom + HOLDINGS_PAGE_SIZE - 1,
    filteredRows.length,
  );
  const paginationSummary = `Showing ${pageFrom}–${pageTo} of ${filteredRows.length} positions`;

  return (
    <HoldingsCard aria-label="Investment holdings">
      <HeaderRow>
        <TitleBlock>
          <Title>Holdings workspace</Title>
          <Subtitle>
            Select a position to inspect value, return and evidence
          </Subtitle>
          <SectionFreshnessNote freshness={freshness} />
        </TitleBlock>
        <Badge>{badgeText}</Badge>
      </HeaderRow>

      {refreshFailed && (
        <NoticeInset>
          <RefreshNotice
            {...REFRESH_NOTICE_COPY.holdings}
            isRetrying={isRefreshing}
            onRetry={() => void retry()}
          />
        </NoticeInset>
      )}

      {/* `role="status"` already implies `aria-live="polite"`. This is the one
          live region the section needs: the per-row badges are notes, so a
          refetch announces this summary rather than queueing every row. */}
      {pendingCount > 0 && (
        <PendingStatus role="status">{pendingMessage}</PendingStatus>
      )}

      {showWorkspaceControls && (
        <HoldingsToolbar
          query={query}
          onQueryChange={setQuery}
          status={status}
          onStatusChange={setStatus}
          assetGroup={assetGroup}
          onAssetGroupChange={setAssetGroup}
          assetOptions={assetOptions}
          isCompact={isMobile}
          sort={sort}
          onSortChange={setSort}
        />
      )}

      <Body>{body}</Body>

      {showWorkspaceControls && pageCount > 1 && (
        <PagerBand>
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={setPage}
            ariaLabel="Holdings pages"
            summary={paginationSummary}
          />
        </PagerBand>
      )}
    </HoldingsCard>
  );
};

export const Holdings = React.memo(HoldingsComponent);
